/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FileText, BookOpen, Building2, Users, ArrowLeft, MessageSquare, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import Page from '../../../../components/Page';
import tenantUserServices from '../../../../services/clientAdmin/tenantUserServices';
import DeactivateLearnerModal from '../../modals/DeactivateLearnerModal';
import ReactivateLearnerModal from '../../modals/ReactivateLearnerModal';
import EditLearnerModal from '../../modals/EditLearnerModal';
import AssessmentTable from './components/AssessmentTable';
import CourseTable from './components/CourseTable';
import ContentTable from './components/ContentTable';
import { SectionSkeleton } from './components/Skeletons';
import { formatDate, getInitials } from './utils/formatters';
import { useLearnerData } from './hooks/useLearnerData';
import adminServices from '../../../../services/adminServices';

export default function LearnerProfile() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('id');

  const {
    profile,
    profileLoading,
    assessments,
    assessmentsLoading,
    assessmentsPage,
    assessmentsTotal,
    fetchAssessments,
    courses,
    coursesLoading,
    coursesPage,
    coursesTotal,
    fetchCourses,
    content,
    contentLoading,
    contentPage,
    contentTotal,
    fetchContent,
    updateProfileStatus,
  } = useLearnerData(userId);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // AI conversation timeline
  const [aiSessions, setAiSessions] = useState([]);
  const [aiSessionsLoading, setAiSessionsLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    if (showDeactivateModal || showReactivateModal || showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showDeactivateModal, showReactivateModal, showEditModal]);
  
  useEffect(() => {
    if (!userId) return;
    adminServices.getAiSessionLogs(userId)
      .then(res => { if (res.ok) setAiSessions(res.data?.sessions || []); })
      .finally(() => setAiSessionsLoading(false));
  }, [userId]);

  const handleDeactivate = async () => {
    const res = await tenantUserServices.toggleTenantUser(userId, false);
    if (res.ok) {
      updateProfileStatus('INACTIVE');
      setShowDeactivateModal(false);
    }
  };

  const handleReactivate = async () => {
    const res = await tenantUserServices.toggleTenantUser(userId, true);
    if (res.ok) {
      updateProfileStatus('ACTIVE');
      setShowReactivateModal(false);
    }
  };

  const pageTitle = profile
    ? `${profile.firstName} ${profile.lastName} — Learner Profile`
    : 'Learner Profile';

  const isActive = profile?.status === 'ACTIVE';

  const getDotClass = (log) => {
    if (log.is_flagged) return 'bg-red-500 text-white';
    if (log.is_out_of_scope) return 'bg-amber-400 text-white';
    return 'bg-blue-500 text-white';
  };

  const getBubbleClass = (log) => {
    if (log.is_flagged) return 'bg-red-50 border border-red-200 text-red-800';
    if (log.is_out_of_scope) return 'bg-amber-50 border border-amber-200 text-amber-800';
    return 'bg-white border border-gray-200 text-gray-700';
  };

  return (
    <Page title={pageTitle}>
      <div className="bg-white min-h-screen">
        {/* Breadcrumb */}
        <div className="px-8 pt-6 pb-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Learners
          </button>
        </div>

        {/* Profile Header */}
        <div className="px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {profileLoading ? (
              <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-slate-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">
                  {getInitials(profile?.firstName, profile?.lastName)}
                </span>
              </div>
            )}
            <div>
              {profileLoading ? (
                <>
                  <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-3.5 w-36 bg-gray-200 rounded animate-pulse" />
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {profile?.firstName} {profile?.lastName}
                  </h1>
                  <p className="text-sm text-gray-500 mt-0.5">{profile?.email}</p>
                </>
              )}
            </div>
          </div>

          {!profileLoading && profile && (
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1.5 text-sm font-medium rounded border ${isActive
                    ? 'text-green-600 border-green-500'
                    : 'text-gray-500 border-gray-400'
                  }`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </span>
              {/* {!isActive && ( */}
                <button
                  type="button"
                  onClick={() => setShowEditModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-purple-600 border border-purple-500
               rounded hover:bg-purple-50 transition-colors"
                >
                  Edit
                </button>
              {/* )} */}
              {isActive ? (
                <button
                  type="button"
                  onClick={() => setShowDeactivateModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-500 rounded hover:bg-red-50 transition-colors"
                >
                  Deactivate
                </button>
              ) : (profile?.lastlogin &&
                <button
                  type="button"
                  onClick={() => setShowReactivateModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-green-600 border border-green-500 rounded hover:bg-green-50 transition-colors"
                >
                  Reactivate
                </button>
              )}
            </div>
          )}
        </div>

        {/* Identity + Organization */}
        <div className="px-8 pb-5 grid grid-cols-5 gap-4">
          <div className="col-span-3 border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Identity</p>
            {profileLoading ? (
              <SectionSkeleton rows={3} cols={2} />
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                {[
                  ['Full Name', `${profile?.firstName} ${profile?.lastName ? profile?.lastName : ''}`],
                  ['Email', profile?.email],
                  ['Phone', profile?.phone || '—'],
                  ['Roll No.', profile?.employeeRollNumber || '—'],
                  ['Joined', formatDate(profile?.createdAt)],
                  ['Notes', profile?.notes || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="text-sm font-semibold text-gray-900 break-words">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="col-span-2 border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Organisation</p>
            {profileLoading ? (
              <div className="space-y-5">
                <div>
                  <div className="h-2.5 w-20 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
                </div>
                <div>
                  <div className="h-2.5 w-16 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Department</p>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-900">
                      {profile?.departmentName || '—'}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Team</p>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-900">
                      {profile?.teamName || '—'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sections */}
        {[
          {
            icon: FileText, label: 'Assessment Performance', count: assessmentsTotal, loading: assessmentsLoading,
            component: (
              <AssessmentTable
                data={assessments}
                loading={assessmentsLoading}
                currentPage={assessmentsPage}
                totalCount={assessmentsTotal}
                onPageChange={fetchAssessments}
              />
            )
          },
          {
            icon: BookOpen, label: 'Course Performance', count: coursesTotal, loading: coursesLoading,
            component: (
              <CourseTable
                data={courses}
                loading={coursesLoading}
                currentPage={coursesPage}
                totalCount={coursesTotal}
                onPageChange={fetchCourses}
              />
            )
          },
          // {
          //   icon: Hash, label: 'Content Access', count: contentTotal, loading: contentLoading,
          //   component: (
          //     <ContentTable
          //       data={content}
          //       loading={contentLoading}
          //       currentPage={contentPage}
          //       totalCount={contentTotal}
          //       onPageChange={fetchContent}
          //     />
          //   )
          // },
        ].map(({ icon: Icon, label, count, loading, component }) => (
          <div key={label} className="px-8 pb-5">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-6 py-3.5 border-b border-gray-100">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">{label}</span>
                {!loading && <span className="text-sm text-gray-400">({count})</span>}
              </div>
              {component}
            </div>
          </div>
        ))}

        {/* AI Conversation Timeline */}
        <div className="px-8 pb-8">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-3.5 border-b border-gray-100">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">AI Conversation Timeline</span>
              {!aiSessionsLoading && <span className="text-sm text-gray-400">({aiSessions.length} sessions)</span>}
            </div>

            {aiSessionsLoading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
              </div>
            ) : null}
            {!aiSessionsLoading && aiSessions.length === 0 && (
              <p className="px-6 py-8 text-center text-sm text-gray-400">No AI conversations yet.</p>
            )}
            {!aiSessionsLoading && aiSessions.length > 0 && (
              <div className="divide-y divide-gray-100">
                {aiSessions.map((session) => {
                  const isOpen = expandedSession === session.session_id;
                  const flaggedCount = (session.logs || []).filter(l => l.is_flagged).length;
                  return (
                    <div key={session.session_id}>
                      <button
                        type="button"
                        onClick={() => setExpandedSession(isOpen ? null : session.session_id)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{session.course_title || 'Unknown Course'}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {formatDate(session.started_at)} · {(session.logs || []).length} messages
                              {flaggedCount > 0 && (
                                <span className="ml-2 inline-flex items-center gap-1 text-red-600 font-semibold">
                                  <ShieldAlert className="w-3 h-3" />{flaggedCount} flagged
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">Session #{session.session_id}</span>
                      </button>

                      {isOpen && (
                        <div className="px-6 pb-5 bg-gray-50">
                          {(!session.logs || session.logs.length === 0) ? (
                            <p className="text-xs text-gray-400 py-3">No messages in this session.</p>
                          ) : (
                            <div className="relative ml-4 border-l-2 border-gray-200 space-y-0">
                              {session.logs.map((log, idx) => (
                                <div key={log.id} className="relative pl-6 py-3">
                                  <span className={`absolute -left-[9px] top-4 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold ${getDotClass(log)}`}>
                                    {idx + 1}
                                  </span>
                                  <div className="mb-1.5">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Q</span>
                                    <p className="text-sm text-gray-800 mt-0.5">{log.question_text}</p>
                                  </div>
                                  <div className={`rounded-lg px-3 py-2 text-sm ${getBubbleClass(log)}`}>
                                    {log.is_flagged && (
                                      <div className="flex items-center gap-1 mb-1">
                                        <ShieldAlert className="w-3 h-3 text-red-500" />
                                        <span className="text-xs font-semibold text-red-600 uppercase">{log.flag_type?.replace(/_/g, ' ')} · {log.flag_severity}</span>
                                      </div>
                                    )}
                                    {log.is_out_of_scope && !log.is_flagged && <span className="text-xs font-semibold text-amber-600 block mb-1">Out of scope</span>}
                                    <p>{log.answer_text}</p>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">{formatDate(log.created_at)}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      {showDeactivateModal && profile && (
        <DeactivateLearnerModal
          learner={profile}
          onClose={() => setShowDeactivateModal(false)}
          onConfirm={handleDeactivate}
        />
      )}
      {showReactivateModal && profile && (
        <ReactivateLearnerModal
          learner={profile}
          onClose={() => setShowReactivateModal(false)}
          onConfirm={handleReactivate}
        />
      )}
      {showEditModal && profile && (
        <EditLearnerModal
          learner={profile}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            navigate(0);
          }}
        />
      )}
    </Page>
  );
}